import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Tv, Play, Sparkles, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
  const [tab, setTab] = useState('iptv'); // 'iptv' | 'email'
  // Email login
  const [email, setEmail] = useState('');
  // IPTV login
  const [xtreamUser, setXtreamUser] = useState('');
  // Shared
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, xtreamLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter your password'); return; }
    if (tab === 'email' && !email) { toast.error('Please enter your email'); return; }
    if (tab === 'iptv' && !xtreamUser) { toast.error('Please enter your IPTV username'); return; }

    setLoading(true);
    try {
      if (tab === 'iptv') {
        await xtreamLogin(xtreamUser, password);
      } else {
        await login(email, password);
      }
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side — Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1739343338040-2dae68f6bdf5?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="mb-8">
            <img src="/logo.png" alt="FTA Xtream" className="h-16 w-auto" />
          </div>
          <h1 className="text-5xl font-bold leading-tight mb-6">
            Your Universe of<br />
            <span className="text-[rgb(124,58,237)]">Entertainment</span>
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-md">
            Live TV, Movies, Series, Sports — all in one place. Log in with your IPTV subscription credentials.
          </p>
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Play className="w-5 h-5 text-[rgb(16,185,129)]" />
              <span className="text-white/80">16,000+ Live Channels</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[rgb(245,158,11)]" />
              <span className="text-white/80">42,000+ VOD Titles</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side — Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[rgb(5,5,5)]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-10">
            <img src="/logo.png" alt="FTA Xtream" className="h-12 w-auto" />
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2">Sign In</h2>
            <p className="text-[rgb(161,161,170)]">Access your IPTV subscription</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex rounded-xl bg-[rgb(20,20,22)] p-1 mb-8 gap-1" data-testid="login-tab-switcher">
            <button
              type="button"
              onClick={() => setTab('iptv')}
              data-testid="tab-iptv"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === 'iptv'
                  ? 'bg-[rgb(124,58,237)] text-white shadow-lg'
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              <Wifi className="w-4 h-4" />
              IPTV Account
            </button>
            <button
              type="button"
              onClick={() => setTab('email')}
              data-testid="tab-email"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === 'email'
                  ? 'bg-[rgb(124,58,237)] text-white shadow-lg'
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              <Tv className="w-4 h-4" />
              Admin / Email
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {tab === 'iptv' ? (
              <div className="space-y-2">
                <Label htmlFor="xtream-user" className="text-white/80">IPTV Username</Label>
                <Input
                  id="xtream-user"
                  type="text"
                  placeholder="Your IPTV username"
                  value={xtreamUser}
                  onChange={(e) => setXtreamUser(e.target.value)}
                  className="h-12 bg-[rgb(31,31,34)] border-[rgb(39,39,42)] text-white placeholder:text-white/30 focus:border-[rgb(124,58,237)]"
                  data-testid="iptv-username-input"
                  autoComplete="username"
                />
                <p className="text-xs text-white/40">Use the username provided with your subscription</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[rgb(31,31,34)] border-[rgb(39,39,42)] text-white placeholder:text-white/30 focus:border-[rgb(124,58,237)]"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-[rgb(31,31,34)] border-[rgb(39,39,42)] text-white placeholder:text-white/30 focus:border-[rgb(124,58,237)] pr-12"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[rgb(124,58,237)] hover:bg-[rgb(109,40,217)] text-white font-semibold rounded-full transition-all btn-press glow-primary mt-2"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : tab === 'iptv' ? (
                'Connect to IPTV'
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {tab === 'email' && (
            <p className="mt-8 text-center text-[rgb(161,161,170)]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[rgb(124,58,237)] hover:underline font-medium" data-testid="register-link">
                Create one
              </Link>
            </p>
          )}

          {tab === 'iptv' && (
            <p className="mt-6 text-center text-xs text-white/30">
              Contact your IPTV provider for login credentials
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
